import { useState } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Ticket, 
  Search, 
  Code2, 
  GitBranch, 
  Shield, 
  Menu,
  X,
  BarChart3,
  Settings as SettingsIcon,
  Sparkles,
  CheckSquare,
  Rocket,
  AlertTriangle,
  BookOpen,
  FileText,
  Network,
  Target,
  ChevronRight,
  ChevronDown,
  LogOut,
  Zap,
  PackageSearch,
  ShieldAlert,
  ArrowLeftRight,
  Building2,
  Clock,
} from 'lucide-react';
import { formatGroupLabels } from "@/lib/rbacGroups";
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import ThemeToggle from '@/components/ThemeToggle';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { PermissionsProvider, usePermissions } from '@/contexts/PermissionsContext';
import { useHiddenFlags, useWorkflowFlags } from '@/contexts/FeatureFlagContext';
import { isNavItemVisibleByFlags } from '@/lib/navFeatureFlags';
import ProtectedRoute from '@/components/ProtectedRoute';
import Unauthorized401Bridge from '@/lib/Unauthorized401Bridge';
import { canAccessNavPath } from '@/lib/rbacRoutes';
import { isPathVisibleByWorkflowFlags } from '@/lib/workflowFeatureFlags';

// Import pages
import Login from '@/pages/Login';
import EntraCallback from '@/pages/EntraCallback';
import { isEntraCallbackPath } from '@/lib/entraAuth';
import Dashboard from '@/pages/Dashboard';
import Tickets from '@/pages/Tickets';
import FfidTickets from '@/pages/FfidTickets';
import TicketDetail from '@/pages/TicketDetail';
import EnhancedTicketDetail from '@/pages/EnhancedTicketDetail';
import ErrorTicketDetail from '@/pages/ErrorTicketDetail';
import TicketDetailRouter from '@/pages/TicketDetailRouter';
import WorkflowTicketDetail from '@/pages/WorkflowTicketDetail';
import AbapCodeTransports from '@/pages/AbapCodeTransports';
import AbapTransportDetail from '@/pages/AbapTransportDetail';
import DiscoveryEngine from '@/pages/DiscoveryEngine';
import VibeChatWorkspace from '@/pages/VibeChatWorkspace';
import CodeEditor from '@/pages/CodeEditor';
import Governance from '@/pages/Governance';
import DeploymentStudio from '@/pages/DeploymentStudio';
import ErrorResolution from '@/pages/ErrorResolution';
import Analytics from '@/pages/Analytics';
import Settings from '@/pages/Settings';
import AgentStudio from '@/pages/AgentStudio';
import WorkflowTemplates from '@/pages/WorkflowTemplates';
import ApprovalWorkflow from '@/pages/ApprovalWorkflow';
import Documentation from '@/pages/Documentation';
import Reports from '@/pages/Reports';
import BusinessValue from '@/pages/BusinessValue';
import TriggerWorkflow from '@/pages/TriggerWorkflow';
import SodRules from '@/pages/SodRules';
import SapJobMonitoring from '@/pages/SapJobMonitoring';
import SapJobDetail from '@/pages/SapJobDetail';
import SapJobRunDetail from '@/pages/SapJobRunDetail';
import Aura from '@/pages/Aura';
import AuraProjectDetail from '@/pages/AuraProjectDetail';
import AuraAgentStudio from '@/pages/AuraAgentStudio';
import TransformationsCompaniesAdmin from '@/pages/TransformationsCompaniesAdmin';
import AccessDenied from '@/pages/AccessDenied';
import PendingAccess from '@/pages/PendingAccess';
import InfoAssistantWidget from '@/components/InfoAssistantWidget';
import {
  HIDE_DASHBOARD_FLAG,
  HIDE_TICKETS_FLAG,
  HIDE_FFID_FLAG,
  HIDE_TRIGGER_WORKFLOW_FLAG,
  HIDE_AGENT_STUDIO_FLAG,
  HIDE_VIBE_CODING_STUDIO_FLAG,
  HIDE_DISCOVERY_FLAG,
  HIDE_DEPLOYMENT_STUDIO_FLAG,
  HIDE_ERROR_RESOLUTION_FLAG,
  HIDE_APPROVALS_FLAG,
  HIDE_ANALYTICS_FLAG,
  HIDE_BUSINESS_VALUE_FLAG,
  HIDE_REPORTS_FLAG,
  HIDE_DOCUMENTATION_FLAG,
  HIDE_SETTINGS_FLAG,
  HIDE_SOD_RULES_FLAG,
  HIDE_ABAP_TRANSPORTS_FLAG,
  HIDE_AURA_FLAG,
} from '@/lib/navFeatureFlags';

// Navigation categories (full parity with juno-halo frontend layout).
const navCategories = [
  {
    label: 'Core Workflow',
    items: [
      { path: '/', icon: LayoutDashboard, label: 'Dashboard', testId: 'nav-dashboard', featureFlag: HIDE_DASHBOARD_FLAG },
      { path: '/tickets', icon: Ticket, label: 'Tickets', testId: 'nav-tickets', featureFlag: HIDE_TICKETS_FLAG },
      { path: '/ffid', icon: ShieldAlert, label: 'Firefighter ID', testId: 'nav-ffid', featureFlag: HIDE_FFID_FLAG },
      { path: '/trigger-workflow', icon: Zap, label: 'Trigger Workflow', testId: 'nav-trigger-workflow', featureFlag: HIDE_TRIGGER_WORKFLOW_FLAG },
    ]
  },
  {
    label: 'AI & Automation',
    items: [
      { path: '/agent-studio', icon: Sparkles, label: 'Agent Studio', testId: 'nav-agent-studio', featureFlag: HIDE_AGENT_STUDIO_FLAG },
      { path: '/workflow-templates', icon: GitBranch, label: 'Workflow Templates', testId: 'nav-workflow-templates', featureFlag: HIDE_AGENT_STUDIO_FLAG },
      { path: '/vibe-studio', icon: Code2, label: 'Vibe Coding Studio', testId: 'nav-vibe-studio', featureFlag: HIDE_VIBE_CODING_STUDIO_FLAG },
    ]
  },
  {
    label: 'Operations',
    items: [
      { path: '/discovery', icon: Search, label: 'Discovery Engine', testId: 'nav-discovery', featureFlag: HIDE_DISCOVERY_FLAG },
      { path: '/deployment', icon: Rocket, label: 'Deployment Studio', testId: 'nav-deployment', featureFlag: HIDE_DEPLOYMENT_STUDIO_FLAG },
      { path: '/abap-transports', icon: PackageSearch, label: 'ABAP Code Transports', testId: 'nav-abap-transports', featureFlag: HIDE_ABAP_TRANSPORTS_FLAG },
      { path: '/errors', icon: AlertTriangle, label: 'Error Resolution', testId: 'nav-errors', featureFlag: HIDE_ERROR_RESOLUTION_FLAG },
      { path: '/approvals', icon: CheckSquare, label: 'Approvals', testId: 'nav-approvals', featureFlag: HIDE_APPROVALS_FLAG },
      { path: '/sap-job-monitoring', icon: Clock, label: 'SAP Job Monitoring', testId: 'nav-sap-job-monitoring' },
    ]
  },
  {
    label: 'Transformations',
    items: [
      {
        path: '/transformations',
        icon: ArrowLeftRight,
        label: 'Dashboard',
        testId: 'nav-transformations',
        featureFlag: HIDE_AURA_FLAG,
        badge: 'NEW',
        // Don't highlight when on Companies admin (prefix conflict).
        activeWhen: (pathname) =>
          pathname === '/transformations'
          || pathname.startsWith('/transformations/projects')
          || pathname.startsWith('/transformations/studio'),
      },
      {
        path: '/transformations/companies',
        icon: Building2,
        label: 'Companies',
        testId: 'nav-transformations-companies',
        featureFlag: HIDE_AURA_FLAG,
      },
    ]
  },
  {
    label: 'Insights',
    items: [
      { path: '/analytics', icon: BarChart3, label: 'Analytics', testId: 'nav-analytics', featureFlag: HIDE_ANALYTICS_FLAG },
      { path: '/business-value', icon: Target, label: 'Business Value', testId: 'nav-business-value', featureFlag: HIDE_BUSINESS_VALUE_FLAG },
      { path: '/reports', icon: FileText, label: 'Reports', testId: 'nav-reports', featureFlag: HIDE_REPORTS_FLAG },
      { path: '/documentation', icon: BookOpen, label: 'Documentation', testId: 'nav-documentation', featureFlag: HIDE_DOCUMENTATION_FLAG },
    ]
  },
  {
    label: 'Configuration',
    items: [
      { path: '/settings', icon: SettingsIcon, label: 'Settings', testId: 'nav-settings', featureFlag: HIDE_SETTINGS_FLAG },
      { path: '/sod-rules', icon: Shield, label: 'SOD Rules', testId: 'nav-sod-rules', featureFlag: HIDE_SOD_RULES_FLAG },
    ]
  }
];

function Sidebar({ isOpen, setIsOpen, isCollapsed, setIsCollapsed }) {
  const location = useLocation();
  const hiddenFlags = useHiddenFlags();
  const workflowFlags = useWorkflowFlags();
  const { hasPermission } = usePermissions();
  const [expandedCategories, setExpandedCategories] = useState(
    navCategories.reduce((acc, cat) => ({ ...acc, [cat.label]: true }), {})
  );

  const isNavItemVisible = (item) => {
    if (!isNavItemVisibleByFlags(item.featureFlag, hiddenFlags)) {
      return false;
    }
    if (!isPathVisibleByWorkflowFlags(item.path, workflowFlags)) {
      return false;
    }
    return canAccessNavPath(item.path, hasPermission);
  };

  const visibleCategories = navCategories
    .map((category) => ({
      ...category,
      items: category.items.filter(isNavItemVisible),
    }))
    .filter((category) => category.items.length > 0);

  const toggleCategory = (label) => {
    setExpandedCategories(prev => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-[#050813cc] backdrop-blur-sm z-40 lg:hidden" 
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full cp-shell-sidebar z-50 transition-all duration-300
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:static lg:z-0
          ${isCollapsed ? 'lg:w-16' : 'lg:w-64'}
          w-64 flex flex-col
        `}
        data-testid="sidebar"
      >
        <div className={`border-b border-[var(--line)] ${isCollapsed ? 'p-3' : 'p-6'}`}>
          <div className="flex items-center justify-between">
            {!isCollapsed && (
              <div>
                <h1 className="text-[16px] font-bold tracking-tight text-white">Halo</h1>
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[var(--faint)] mt-1">SAP AI Customization Engine</p>
              </div>
            )}
            {isCollapsed && (
              <div className="w-full flex justify-center">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center font-bold text-sm text-white bg-cp-grad shadow-cp-accent">H</div>
              </div>
            )}
            <button
              onClick={() => setIsOpen(false)}
              className="lg:hidden text-[var(--faint)] hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <nav className={`flex-1 overflow-y-auto ${isCollapsed ? 'p-2' : 'p-4'} space-y-1`}>
          {visibleCategories.map((category) => {
            const isExpanded = expandedCategories[category.label];

            return (
              <div key={category.label} className="mb-4">
                {/* Category Header — hidden in collapsed mode */}
                {!isCollapsed && (
                  <button
                    onClick={() => toggleCategory(category.label)}
                    className="w-full flex items-center justify-between px-3 py-2 text-[10.5px] font-semibold text-[var(--faint)] uppercase tracking-[0.08em] hover:text-[var(--text-muted)] transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span>{category.label}</span>
                      {category.badge && (
                        <span className="px-2 py-0.5 text-white text-[10.5px] rounded-pill bg-cp-grad">
                          {category.badge}
                        </span>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                )}

                {/* Category Items */}
                {(isExpanded || isCollapsed) && (
                  <div className={`${isCollapsed ? '' : 'mt-1'} space-y-1`}>
                    {category.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = typeof item.activeWhen === 'function'
                        ? item.activeWhen(location.pathname)
                        : (
                          location.pathname === item.path
                          || location.pathname.startsWith(`${item.path}/`)
                        );

                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setIsOpen(false)}
                          title={isCollapsed ? item.label : undefined}
                          className={`
                            flex items-center rounded-[9px] transition-all duration-150 text-[13px]
                            ${isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-4 py-2.5'}
                            ${isActive
                              ? 'cp-nav-active'
                              : 'text-[var(--text-muted)] hover:bg-[var(--inset)] hover:text-white'
                            }
                          `}
                          data-testid={item.testId}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          {!isCollapsed && (
                            <>
                              <span className="font-medium flex-1">{item.label}</span>
                              {item.badge && (
                                <span
                                  className={`
                                    ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide
                                    ${isActive
                                      ? 'bg-white/20 text-white'
                                      : 'bg-cp-grad text-white shadow-cp-accent'}
                                  `}
                                >
                                  {item.badge}
                                </span>
                              )}
                            </>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className={`border-t border-[var(--line)] ${isCollapsed ? 'p-2' : 'p-4'}`}>
          <UserSection isCollapsed={isCollapsed} />
        </div>
      </aside>
    </>
  );
}

function UserSection({ isCollapsed }) {
  const { user, logout, isAdmin, idpMode } = useAuth();
  const { groups: permGroups, loading: permLoading } = usePermissions();
  const roleLabel = permLoading
    ? "Loading access…"
    : permGroups.length
      ? formatGroupLabels(permGroups)
      : "No access assigned";
  const logoutTitle =
    idpMode === "entra"
      ? "Sign out of Halo. Your Microsoft account may stay signed in."
      : "Sign out";

  if (isCollapsed) {
    return (
      <div className="flex flex-col items-center gap-2">
        <div
          className="h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium bg-cp-grad shadow-cp-accent"
          title={user?.email || user?.username || 'User'}
        >
          {(user?.email || user?.username || '?').charAt(0).toUpperCase()}
        </div>
        <button
          onClick={logout}
          className="flex items-center justify-center p-1.5 text-[var(--faint)] hover:text-white hover:bg-[var(--inset)] rounded-[9px] transition-colors"
          title={logoutTitle}
          data-testid="logout-btn"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 p-2 rounded-[9px] bg-[var(--inset)] border border-[var(--line)]">
        <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium bg-cp-grad">
          {(user?.email || user?.username || '?').charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white truncate">{user?.email || user?.username || 'User'}</div>
          <div className={`text-xs ${isAdmin ? 'text-[var(--mod)]' : 'text-[var(--faint)]'}`}>{roleLabel}</div>
        </div>
      </div>
      <button
        onClick={logout}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--faint)] hover:text-white hover:bg-[var(--inset)] rounded-[9px] transition-colors"
        data-testid="logout-btn"
      >
        <LogOut className="h-4 w-4" />
        <span>Sign out</span>
      </button>
      {idpMode === "entra" && (
        <p className="text-[10px] text-[var(--faint)] leading-snug">
          Halo sign-out does not end your Microsoft session.
        </p>
      )}
      <div className="text-[10.5px] text-[var(--faint)] pt-2 border-t border-[var(--line)]">
        <div className="font-semibold uppercase tracking-[0.06em]">Version 1.0.0</div>
        <div className="mt-1.5 flex items-center gap-1.5 cp-live on">
          <div className="cp-live-dot" />
          <span>All systems operational</span>
        </div>
      </div>
    </div>
  );
}

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="flex h-screen bg-transparent">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} isCollapsed={sidebarCollapsed} setIsCollapsed={setSidebarCollapsed} />
      
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="cp-shell-header px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Mobile open button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-cp-muted hover:text-cp-fg"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-6 w-6" />
            </button>
            {/* Desktop collapse toggle */}
            <button
              onClick={() => setSidebarCollapsed((c) => !c)}
              className="hidden lg:flex items-center justify-center text-cp-muted hover:text-cp-fg"
              title={sidebarCollapsed ? 'Expand nav' : 'Collapse nav'}
            >
              <Menu className="h-6 w-6" />
            </button>
            
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-cp-grad flex items-center justify-center shadow-cp-accent">
                <GitBranch className="h-3.5 w-3.5 text-white" />
              </div>
              <span className="font-semibold text-[15px] tracking-wide text-cp-fg">HALO</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {/* Powered by Logo */}
            <a 
              href="https://www.resolvetech.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity" 
              data-testid="powered-by-logo"
            >
              <span className="text-sm text-cp-muted font-medium">Powered by</span>
              <img 
                src="https://customer-assets.emergentagent.com/job_ai-customizer-1/artifacts/ziz1wre7_rtslogo-with-name-transparent-300-ppi-RGB.png" 
                alt="Resolve Tech Solutions" 
                className="h-8 object-contain dark:brightness-0 dark:invert dark:opacity-80"
              />
            </a>
          </div>
        </header>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto p-6">
          {children}
        </div>
      </main>
    </div>
  );
}

function ProtectedLayout({ children }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

/** Old /aura project bookmarks → Transformations */
function LegacyAuraProjectRedirect() {
  const { projectId } = useParams();
  return <Navigate to={`/transformations/projects/${projectId}`} replace />;
}

function App() {
  return (
    <NotificationProvider>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/access-denied" element={<AccessDenied />} />
            <Route path="/pending-access" element={<PendingAccess />} />
            <Route path="/" element={<ProtectedLayout><Dashboard /></ProtectedLayout>} />
            <Route path="/tickets" element={<ProtectedLayout><Tickets /></ProtectedLayout>} />
            <Route path="/tickets/:id" element={<ProtectedLayout><TicketDetailRouter /></ProtectedLayout>} />
            <Route path="/ffid" element={<ProtectedLayout><FfidTickets /></ProtectedLayout>} />
            <Route path="/ffid/:id" element={<ProtectedLayout><WorkflowTicketDetail /></ProtectedLayout>} />
            <Route path="/tickets-old/:id" element={<ProtectedLayout><EnhancedTicketDetail /></ProtectedLayout>} />
            <Route path="/agent-studio" element={<ProtectedLayout><AgentStudio /></ProtectedLayout>} />
            <Route path="/workflow-templates" element={<ProtectedLayout><WorkflowTemplates /></ProtectedLayout>} />
            <Route path="/discovery" element={<ProtectedLayout><DiscoveryEngine /></ProtectedLayout>} />
            {/* Transformations (Halo) — primary routes */}
            <Route path="/transformations" element={<ProtectedLayout><Aura /></ProtectedLayout>} />
            <Route path="/transformations/companies" element={<ProtectedLayout><TransformationsCompaniesAdmin /></ProtectedLayout>} />
            <Route path="/transformations/studio" element={<ProtectedLayout><AuraAgentStudio /></ProtectedLayout>} />
            <Route path="/transformations/projects/:projectId" element={<ProtectedLayout><AuraProjectDetail /></ProtectedLayout>} />
            {/* Legacy /aura URLs → Transformations */}
            <Route path="/aura" element={<Navigate to="/transformations" replace />} />
            <Route path="/aura/studio" element={<Navigate to="/transformations/studio" replace />} />
            <Route path="/aura/projects/:projectId" element={<LegacyAuraProjectRedirect />} />
            <Route path="/vibe-studio/past-sessions" element={<Navigate to="/vibe-studio" replace />} />
            <Route path="/vibe-studio/chat" element={<Navigate to="/vibe-studio" replace />} />
            <Route path="/vibe-studio" element={<ProtectedLayout><VibeChatWorkspace /></ProtectedLayout>} />
            <Route path="/vibe-studio/:ticketId" element={<ProtectedLayout><CodeEditor /></ProtectedLayout>} />
            <Route path="/trigger-workflow" element={<ProtectedLayout><TriggerWorkflow /></ProtectedLayout>} />
            <Route path="/approvals" element={<ProtectedLayout><ApprovalWorkflow /></ProtectedLayout>} />
            <Route path="/deployment" element={<ProtectedLayout><DeploymentStudio /></ProtectedLayout>} />
            <Route path="/abap-transports" element={<ProtectedLayout><AbapCodeTransports /></ProtectedLayout>} />
            <Route path="/abap-transports/:id" element={<ProtectedLayout><AbapTransportDetail /></ProtectedLayout>} />
            <Route path="/errors" element={<ProtectedLayout><ErrorResolution /></ProtectedLayout>} />
            <Route path="/governance" element={<ProtectedLayout><Governance /></ProtectedLayout>} />
            <Route path="/analytics" element={<ProtectedLayout><Analytics /></ProtectedLayout>} />
            <Route path="/business-value" element={<ProtectedLayout><BusinessValue /></ProtectedLayout>} />
            <Route path="/reports" element={<ProtectedLayout><Reports /></ProtectedLayout>} />
            <Route path="/documentation" element={<ProtectedLayout><Documentation /></ProtectedLayout>} />
            <Route path="/settings" element={<ProtectedLayout><Settings /></ProtectedLayout>} />
            <Route path="/sod-rules" element={<ProtectedLayout><SodRules /></ProtectedLayout>} />
            <Route path="/sap-job-monitoring" element={<ProtectedLayout><SapJobMonitoring /></ProtectedLayout>} />
            <Route path="/sap-job-monitoring/jobs/*" element={<ProtectedLayout><SapJobDetail /></ProtectedLayout>} />
            <Route path="/sap-job-monitoring/runs/*" element={<ProtectedLayout><SapJobRunDetail /></ProtectedLayout>} />
          </Routes>
        </Layout>
        <InfoAssistantWidget />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </NotificationProvider>
  );
}

function AppWithAuth() {
  const { isAuthenticated, loading } = useAuth();

  if (typeof window !== "undefined" && isEntraCallbackPath()) {
    return <EntraCallback />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-cp-page flex items-center justify-center">
        <div className="text-cp-fg flex items-center gap-2">
          <span className="cp-live on inline-flex"><span className="cp-live-dot" /></span>
          Loading…
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return <App />;
}

function AppWrapper() {
  return (
    <AuthProvider>
      <PermissionsProvider>
        <Unauthorized401Bridge>
          <AppWithAuth />
        </Unauthorized401Bridge>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default AppWrapper;
